import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { createClient } from "@supabase/supabase-js";
import toast from "react-hot-toast";
import Topbar from "./Topbar";
import Loader from "./Loader";
import { UserPlus, X, UserRound, Rocket } from "lucide-react";
import TimeRemain from "./TimeRemain";
import trophyBanner from "./assets/matchup-trophy-banner.png";
const apiBase = process.env.REACT_APP_API_URL;
const supabase = createClient(process.env.REACT_APP_SUPABASE_URL, process.env.REACT_APP_SUPABASE_KEY);

export default function PreMatchPrepPage() {
  const { leagueId } = useParams();
  const [team, setTeam] = useState([]);
  const [boosts, setBoosts] = useState([]);
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(true);
  const [prepLocked, setPrepLocked] = useState(false);
  const [activeWeek, setActiveWeek] = useState([]);
  const [lockReason, setLockReason] = useState("");
  const [openBoostFor, setOpenBoostFor] = useState(null);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState({
    open: false,
    type: "success", // "success" | "error"
    message: "",
  });
  const ROLE_SLOTS = ["Top", "Jng", "Mid", "Bot", "Sup"];

  const slipRef = useRef(null);

  useEffect(() => {
    if (loading || prepLocked) return;
    if (!slipRef.current) return;

    const el = slipRef.current;

    const updateHeight = () => {
      const rect = el.getBoundingClientRect();
      document.documentElement.style.setProperty("--slip-height", `${Math.ceil(rect.height)}px`);
    };

    requestAnimationFrame(updateHeight);

    const observer = new ResizeObserver(() => {
      requestAnimationFrame(updateHeight);
    });

    observer.observe(el);

    return () => observer.disconnect();
  }, [loading, prepLocked]);

  const sortByRole = useCallback((a, b) => {
    const roleOrder = ["Top", "Jng", "Mid", "Bot", "Sup"];
    return roleOrder.indexOf(a.players.role) - roleOrder.indexOf(b.players.role);
  }, []);

  const starters = useMemo(() => team.filter((p) => p.is_starter).sort(sortByRole), [team, sortByRole]);

  const bench = useMemo(() => [...team].sort(sortByRole), [team, sortByRole]);

  const filteredBench = useMemo(() => {
    if (!search.trim()) return bench;

    return bench.filter((p) => p.players.name.toLowerCase().includes(search.toLowerCase()));
  }, [bench, search]);

  useEffect(() => {
    const loadPrepData = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) return (window.location.href = "/login");

      setToken(session.access_token);

      const res = await axios.get(`${apiBase}/api/league/${leagueId}/prep`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (res.data.success) {
        setPrepLocked(res.data.prep_locked === true);
        setActiveWeek(res.data.target_week || []);
        setLockReason(res.data.lock_reason || "Matchups for this week have already started.");
        const normalizedPlayers = (res.data.team.user_fantasy_team_players || []).map((p) => {
          // 1. Dig into p.players to find the skin array
          const equippedSkins = p.players?.user_equipped_player_skins;

          // 2. Access the nested image_url correctly
          const skinImageUrl = equippedSkins?.[0]?.player_skins?.full_preview;

          const skinUrl = skinImageUrl ? `/players/${skinImageUrl}` : `/players/${p.players.player_image}`; // fallback to default image

          return {
            ...p,
            equipped_skin_image: skinUrl,
            is_starter: p.is_starter ?? false,
          };
        });

        const requiredRoles = ["Top", "Jng", "Mid", "Bot", "Sup"];

        const starters = [];
        const bench = [];

        normalizedPlayers.forEach((p) => {
          if (p.is_starter !== null && p.is_starter !== undefined) {
            // Already assigned in DB
            if (p.is_starter) starters.push(p);
            else bench.push(p);
          } else {
            // Assign by role if not assigned yet
            if (requiredRoles.includes(p.players.role) && !starters.find((s) => s.players.role === p.players.role)) {
              starters.push({ ...p, is_starter: true });
            } else {
              bench.push({ ...p, is_starter: false });
            }
          }
        });

        // Combine list back
        setTeam([...starters, ...bench]);
        setBoosts(res.data.boosts || []);
      }
      setLoading(false);
    };

    loadPrepData();
  }, [leagueId]);

  // Toggle starter/bench with validation
  const toggleStarter = (playerId) => {
    setTeam((prev) => {
      const updated = prev.map((p) => {
        if (p.player_id !== playerId) return p;

        const becomingBench = p.is_starter === true;

        return {
          ...p,
          is_starter: !p.is_starter,
          boost_id: becomingBench ? "no_boost" : p.boost_id, // reset boost if benching
        };
      });

      // Enforce exactly 5 starters
      const starters = updated.filter((p) => p.is_starter);
      if (starters.length > 5) {
        toast.error("This role already has a starter");
        return prev;
      }

      // Validate role requirements
      const requiredRoles = ["Top", "Jng", "Mid", "Bot", "Sup"];
      const rolesInStarters = starters.map((p) => p.players.role);

      if (starters.length === 5 && requiredRoles.some((role) => !rolesInStarters.includes(role))) {
        toast.error("Starters must include Top, Jungle, Mid, Bot, and Support");
        return prev;
      }

      return updated;
    });
  };

  const handleBoostChange = (playerId, boostId) => {
    setTeam((prev) =>
      prev.map((p) => {
        // If assigning a REAL boost (not "no_boost"), remove it from others
        if (boostId !== "no_boost" && p.boost_id === boostId && p.player_id !== playerId) {
          return { ...p, boost_id: "no_boost" };
        }

        // Assign boost to selected player
        if (p.player_id === playerId) {
          return { ...p, boost_id: boostId };
        }

        return p;
      }),
    );
  };

  const handleSave = async () => {
    if (prepLocked) return;
    setSaving(true);
    try {
      const assignments = team.map((p) => ({
        player_id: p.player_id,
        boost_id: p.boost_id,
        is_starter: p.is_starter,
      }));

      await axios.post(`${apiBase}/api/league/${leagueId}/prep/save`, { assignments }, { headers: { Authorization: `Bearer ${token}` } });

      setModal({
        open: true,
        type: "success",
        message: "Your team has been succesfully saved",
      });
      toast.success("Your team has been succesfully saved");
    } catch (err) {
      console.error(err);
      setModal({
        open: true,
        type: "error",
        message: "Failed to save data. Please try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  const boostMap = useMemo(() => {
    return Object.fromEntries(boosts.map((b) => [b.id, b]));
  }, [boosts]);

  const selectedPlayers = starters.length;
  const equippedBoosts = starters.filter((p) => p.boost_id && p.boost_id !== "no_boost").length;

  const requiredPlayers = ROLE_SLOTS.length;
  const requiredBoosts = ROLE_SLOTS.length;

  const lineupComplete = selectedPlayers === requiredPlayers;
  const boostsComplete = equippedBoosts === requiredBoosts;

  const currentStep = !lineupComplete ? 1 : !boostsComplete ? 2 : 3;

  if (prepLocked) {
    return (
      <div className="page-container" style={{ padding: "0" }}>
        <Topbar leagueId={leagueId} showLinks={true} />
        <div
          style={{
            marginTop: 80,
            background: "#020912",
            border: "1px solid #19232f",
            borderRadius: 14,
            padding: 40,
            textAlign: "center",
          }}>
          <h2 style={{ color: "white", fontSize: 28, marginBottom: 12 }}>Matchups Locked</h2>

          <p style={{ color: "#9ca3af", fontSize: 14, maxWidth: 520, margin: "0 auto", lineHeight: 1.6 }}>{lockReason}</p>

          <div style={{ marginTop: 24 }}>
            <a
              href={`/dashboard/league/${leagueId}`}
              style={{
                display: "inline-block",
                padding: "10px 24px",
                borderRadius: 999,
                background: "rgb(50,115,250)",
                color: "white",
                fontWeight: 600,
                textDecoration: "none",
              }}>
              Return Home
            </a>
          </div>
        </div>
      </div>
    );
  }

  const activePlayer = starters.find((player) => player.player_id === openBoostFor);

  if (loading) return <Loader loadingTitle={"Loading Pre-Match Room"} showLogo={true} />;

  return (
    <div className="page-container" style={{ padding: "0" }}>
      <Topbar leagueId={leagueId} showLinks={true} />
      {modal.open && (
        <>
          {/* Backdrop */}
          <div onClick={() => setModal({ ...modal, open: false })} className="modal-overlay">
            {/* Modal */}
            <div className="market-modal" onClick={(e) => e.stopPropagation()}>
              <h3
                style={{
                  margin: "0 0 10px",
                  color: "white",
                }}>
                {modal.type === "success" ? "Success" : "Error"}
              </h3>

              <p style={{ color: "#e5e7eb", fontSize: 14 }}>{modal.message}</p>

              <button
                onClick={() => setModal({ ...modal, open: false })}
                style={{
                  marginTop: 16,
                  width: "100%",
                  padding: 10,
                  borderRadius: 80,
                  border: "none",
                  fontWeight: "bold",
                  cursor: "pointer",
                  background: modal.type === "success" ? "rgb(50, 115, 250)" : "#ef4444",
                  color: "white",
                }}>
                OK
              </button>
            </div>
          </div>
        </>
      )}

      <div className="main-prematch">
        {/* MATCHUP HEADER */}
        <div className="matchup-header">
          <img className="matchup-banner-image" src={trophyBanner} alt="" />
          <div className="matchup-header-top">
            <div className="matchup-heading">
              <span className="matchup-heading-eyebrow">FANTASY TEAM MANAGEMENT</span>

              <h1>
                Matchup Week <span>{activeWeek.week}</span>
              </h1>

              <p>Build your lineup for this week's matches</p>
            </div>

            <div className="matchup-lock-timer">
              <div className="matchup-timer-content">
                {activeWeek && <TimeRemain startDate={activeWeek.week_start_at} subtitle="Locks In" variant="prematch" showSeconds={true} />}
              </div>
            </div>
          </div>

          {/* PROGRESS STEPS */}
          <div className="matchup-progress">
            {[
              { number: 1, label: "Select Your Lineup" },
              { number: 2, label: "Equip Boosts" },
              { number: 3, label: "Review & Lock In" },
            ].map((step) => {
              const isComplete = step.number === 1 ? lineupComplete : step.number === 2 ? boostsComplete : false;

              const isActive = currentStep === step.number;

              return (
                <div
                  key={step.number}
                  className={`matchup-progress-step
            ${isActive ? "active" : ""}
            ${isComplete ? "complete" : ""}
          `}>
                  <div className="matchup-step-indicator">{isComplete && !isActive ? "✓" : step.number}</div>

                  <span className="matchup-step-label">{step.label}</span>

                  <div className="matchup-step-connector" />
                </div>
              );
            })}
          </div>

          {/* PROGRESS SUMMARY */}
          <div className="matchup-progress-summary">
            <div className="matchup-summary-item">
              <span className="matchup-summary-icon players">
                <UserRound fill="currentColor" size={17} />
              </span>

              <span>
                <strong style={{ color: "#20baff" }}>
                  {selectedPlayers}/{requiredPlayers}
                </strong>{" "}
                Players selected
              </span>
            </div>

            <span className="matchup-summary-divider" />

            <div className="matchup-summary-item">
              <span className="matchup-summary-icon boosts">
                <Rocket fill="currentColor" size={17} />
              </span>

              <span>
                <strong style={{ color: "#ffc34d" }}>
                  {equippedBoosts}/{requiredBoosts}
                </strong>{" "}
                Boosts equipped
              </span>
            </div>
          </div>
        </div>

        {/* PLAYER SEARCH */}
        <div className="prematch-search">
          <input type="text" placeholder="Search player..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="training-players-grid">
          {filteredBench.map((p) => {
            const isStarter = p.is_starter;
            return (
              <div key={p.player_id} className={`player-card ${isStarter ? "starter-selected" : ""}`}>
                <img src={`/roles/${p.players.role}.svg`} alt={p.players.role} style={{ width: 16, opacity: 0.6, borderRadius: "0" }} />
                <div
                  className={`player-image-wrapper
    ${p.is_starter ? "starter-selected with-glow" : ""}
    ${p.is_team_eliminated ? "eliminated" : ""}
  `}
                  style={{
                    "--mask-image-src": `url(${p.equipped_skin_image})`,
                    "--glow-color": "#3b82f6",
                  }}>
                  <img
                    src={p.equipped_skin_image}
                    alt={p.players.name}
                    className="player-image"
                    style={{
                      zIndex: 4,
                      width: "142px",
                      cursor: "pointer",
                      border: p.is_starter ? "3px solid #3273fa" : "3px solid rgba(50, 113, 250, 0)",
                      borderRadius: "11px",
                      opacity: p.is_starter ? "0.8" : "1",
                    }}
                    onClick={() => toggleStarter(p.player_id)}
                  />
                </div>
                {p.matches_this_week &&
                  p.matches_this_week.length > 0 &&
                  (() => {
                    const m = p.matches_this_week[0]; // get only the next match
                    const pg = m.projected_points_per_game ?? 0;

                    const background =
                      pg >= 32
                        ? "linear-gradient(53.32deg, #eae3da 25.96%, #bfe6ef 41.34%, #e1e3f5 59.82%, #e6badd 70.04%, #e8f6ef 80.25%)" // God
                        : pg >= 28
                          ? "linear-gradient(135deg, #00f3eb, #00f3eb)" // Elite
                          : pg >= 22
                            ? "linear-gradient(135deg, #25ed36, #25ed36)" // Above Avg
                            : pg >= 20
                              ? "linear-gradient(135deg, #ff7e34, #ff7e34)" // Average
                              : "linear-gradient(135deg, #ef4444, #fca5a5)"; // Poor / Bad

                    const textShadow =
                      pg >= 34
                        ? `0 0 6px #bfe6ef,
         0 0 12px #bfe6ef,
         0 0 24px #bfe6ef,
         0 0 36px #bfe6ef,
         0 0 52px #bfe6ef,
         0 0 64px #bfe6ef`
                        : "";

                    return (
                      <h3
                        className="average-performance-bubble"
                        style={{
                          background,
                          textShadow,
                          border: "1px solid #ffffff00",
                          maxWidth: "fit-content",
                        }}>
                        {pg.toFixed(0)}
                      </h3>
                    );
                  })()}
                <div style={{ display: "flex", flexDirection: " column", width: "100%", gap: "2px" }}>
                  {p.matches_this_week && p.matches_this_week.length > 0 ? (
                    <>
                      <p
                        style={{
                          margin: "0",
                          color: "white",
                          opacity: "0.6",
                          fontSize: "11px",
                          textAlign: "center",
                          borderBottom: "1px solid rgb(25, 35, 47)",
                          paddingBottom: "2px",
                          marginBottom: "2px",
                        }}>
                        Matches this week
                      </p>
                      {p.matches_this_week.map((m) => {
                        return (
                          <>
                            <div className="projected-points" key={m.match_id}>
                              <p style={{ opacity: "0.6" }}>VS</p>
                              <img src={`/teams/${m.opponent_logo}`} alt={m.opponent_name} />
                            </div>
                          </>
                        );
                      })}
                    </>
                  ) : (
                    <>
                      {(p?.is_team_eliminated || p.matches_this_week.length === 0) && (
                        <div
                          style={{
                            fontSize: 12,
                            color: p?.is_team_eliminated ? "white" : "#6b7280",
                            marginTop: 8,
                            textAlign: "center",
                            background: p?.is_team_eliminated ? "rgb(221, 68, 68)" : "",
                            borderRadius: "90px",
                          }}>
                          {p?.is_team_eliminated ? "Team Eliminated" : "No matches this week"}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        {openBoostFor && activePlayer && (
          <>
            <div className="market-modal-overlay" onClick={() => setOpenBoostFor(null)}>
              <div className="market-modal" onClick={(e) => e.stopPropagation()}>
                <div className="market-modal-header">
                  <div>
                    <span className="market-modal-eyebrow">ROSTER MANAGEMENT</span>
                    <h3>Select Boost</h3>
                    <p>Choose a boost for {activePlayer.players.name}.</p>
                  </div>

                  <button className="market-modal-close" onClick={() => setOpenBoostFor(null)} aria-label="Close boost selector">
                    ×
                  </button>
                </div>

                <div className="market-boost-grid">
                  {boosts.map((b) => {
                    const usedByOther = b.id !== "no_boost" && team.some((p2) => p2.boost_id === b.id && p2.player_id !== activePlayer.player_id);

                    const isSelected = activePlayer.boost_id === b.id;

                    return (
                      <button
                        key={b.id}
                        className={`market-boost-option
              ${isSelected ? "selected" : ""}
              ${usedByOther ? "disabled" : ""}
            `}
                        disabled={usedByOther}
                        onClick={() => {
                          if (usedByOther) return;

                          handleBoostChange(activePlayer.player_id, b.id);
                          setOpenBoostFor(null);
                        }}>
                        <img src={`/boosts/${b.item_image}`} alt={b.name} />

                        <div className="market-boost-info">
                          <span className="market-boost-name">{b.name}</span>
                          <span className="market-boost-description">{b.description}</span>
                        </div>

                        {isSelected && <span className="market-boost-check">✓</span>}

                        {usedByOther && <span className="market-boost-used">IN USE</span>}
                      </button>
                    );
                  })}
                </div>

                <div className="market-modal-footer">
                  <button
                    className="market-reset-button"
                    onClick={() => {
                      handleBoostChange(activePlayer.player_id, "no_boost");
                      setOpenBoostFor(null);
                    }}>
                    Remove boost
                  </button>

                  <button className="market-done-button" onClick={() => setOpenBoostFor(null)}>
                    Done
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
        <div className="starting-slip-bar" ref={slipRef}>
          <div className="background-fade" style={{ borderRadius: "0px" }} />
          <div className="starting-slip-container">
            <div className="starting-players-slip">
              {ROLE_SLOTS.map((role) => {
                const p = starters.find((s) => s.players.role === role);

                // PLACEHOLDER
                if (!p) {
                  return (
                    <div key={role} className="player-card placeholder" style={{ gap: 8 }}>
                      <img src={`/roles/${role}.svg`} alt={role} style={{ width: 16, opacity: 0.4, borderRadius: "0" }} />

                      <p
                        style={{
                          margin: 0,
                          fontSize: 11,
                          color: "white",
                          opacity: 0.4,
                        }}>
                        Empty {role}
                      </p>

                      <div
                        className="player-image"
                        style={{
                          background: "rgba(255,255,255,0.06)",
                          border: "1px dashed rgba(255,255,255,0.25)",
                          width: "100%",
                          maxWidth: "110px",
                          aspectRatio: "1 / 1",
                          borderRadius: "8px",
                          position: "relative",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}>
                        <UserPlus color="#ffffff40" />
                      </div>
                    </div>
                  );
                }

                // FILLED ROLE SLOT
                return (
                  <div key={p.player_id} className="player-card" style={{ gap: 8 }}>
                    <img src={`/roles/${p.players.role}.svg`} alt={p.players.role} style={{ width: 12, borderRadius: "0" }} />

                    <p style={{ margin: 0, fontSize: 10, color: "white", fontWeight: "600" }}>{p.players.name}</p>
                    <div style={{ position: "relative", display: "inline-block" }}>
                      <img
                        src={p.equipped_skin_image}
                        alt={p.players.name}
                        className="player-image"
                        style={{ border: "3px solid #3273fa", cursor: "pointer", boxSizing: "border-box" }}
                        onClick={() => toggleStarter(p.player_id)}
                      />

                      <X
                        size={16}
                        color="white"
                        onClick={() => toggleStarter(p.player_id)}
                        style={{
                          position: "absolute",
                          top: -6,
                          right: -6,
                          borderRadius: "50%",
                          background: "#ea3943",
                          padding: 2,
                          cursor: "pointer",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.4)",
                        }}
                      />
                    </div>

                    <div style={{ position: "relative" }}>
                      <div
                        onClick={() => setOpenBoostFor(openBoostFor === p.player_id ? null : p.player_id)}
                        className="boost-info"
                        style={{
                          cursor: "pointer",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          marginTop: "-6px",
                        }}>
                        {p.boost_id ? (
                          <img
                            src={`/boosts/${boostMap[p.boost_id]?.item_image}`}
                            style={{ width: 32, height: 32, borderRadius: 4 }}
                            alt={p.boost_id}
                          />
                        ) : (
                          <span style={{ fontSize: 12, color: "#9ca3af" }}>No Boost</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <button disabled={saving} onClick={handleSave} className="edit-btn">
              {saving ? "Saving..." : "Save Lineup"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
